<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { formatDistanceToNow, format } from 'date-fns';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  companyId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const activities = ref([]);

const ACTIVITY_ICONS = {
  call: 'i-lucide-phone',
  email: 'i-lucide-mail',
  meeting: 'i-lucide-calendar',
  note: 'i-lucide-sticky-note',
  task: 'i-lucide-check-square',
  message: 'i-lucide-message-circle',
  stage_changed: 'i-lucide-git-branch',
  deal_won: 'i-lucide-trophy',
  deal_lost: 'i-lucide-x-circle',
  deal_created: 'i-lucide-plus-circle',
  contact_added: 'i-lucide-user-plus',
  contact_removed: 'i-lucide-user-minus',
  company_updated: 'i-lucide-building-2',
  default: 'i-lucide-activity',
};

const ACTIVITY_COLORS = {
  call: 'text-blue-500 bg-blue-500/10',
  email: 'text-purple-500 bg-purple-500/10',
  meeting: 'text-green-500 bg-green-500/10',
  note: 'text-yellow-500 bg-yellow-500/10',
  task: 'text-orange-500 bg-orange-500/10',
  message: 'text-cyan-500 bg-cyan-500/10',
  stage_changed: 'text-indigo-500 bg-indigo-500/10',
  deal_won: 'text-emerald-500 bg-emerald-500/10',
  deal_lost: 'text-red-500 bg-red-500/10',
  deal_created: 'text-teal-500 bg-teal-500/10',
  contact_added: 'text-sky-500 bg-sky-500/10',
  contact_removed: 'text-rose-500 bg-rose-500/10',
  company_updated: 'text-slate-500 bg-slate-500/10',
  default: 'text-n-slate-11 bg-n-alpha-2',
};

const getActivityIcon = type => ACTIVITY_ICONS[type] || ACTIVITY_ICONS.default;

const getActivityColor = type =>
  ACTIVITY_COLORS[type] || ACTIVITY_COLORS.default;

const formatActivityDate = date => {
  if (!date) return '';
  const activityDate = new Date(date);
  const now = new Date();
  const diffInDays = Math.floor((now - activityDate) / (1000 * 60 * 60 * 24));

  if (diffInDays < 7) {
    return formatDistanceToNow(activityDate, { addSuffix: true });
  }
  return format(activityDate, 'MMM d, yyyy');
};

const fetchActivities = async () => {
  isLoading.value = true;
  try {
    const response = await store.dispatch('activities/forCompany', {
      companyId: props.companyId,
    });
    activities.value = response || [];
  } catch (error) {
    activities.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchActivities();
});
</script>

<template>
  <div class="flex flex-col h-full px-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('COMPANY_DETAILS.SIDEBAR.TIMELINE.TITLE') }}
      </h3>
      <Button
        icon="i-lucide-refresh-cw"
        size="xs"
        ghost
        slate
        :disabled="isLoading"
        @click="fetchActivities"
      />
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>

    <div
      v-else-if="activities.length === 0"
      class="flex flex-col items-center justify-center py-8 text-center"
    >
      <Icon icon="i-lucide-activity" size="size-8 text-n-slate-9 mb-2" />
      <span class="text-sm text-n-slate-11">
        {{ t('COMPANY_DETAILS.SIDEBAR.TIMELINE.EMPTY') }}
      </span>
    </div>

    <div v-else class="flex flex-col gap-1 overflow-y-auto">
      <div
        v-for="(activity, index) in activities"
        :key="activity.id"
        class="relative flex gap-3 pb-4"
      >
        <!-- Timeline line -->
        <div
          v-if="index < activities.length - 1"
          class="absolute w-px h-full bg-n-weak left-4 top-8"
        />

        <!-- Icon -->
        <div
          class="relative z-10 flex items-center justify-center flex-shrink-0 rounded-full size-8"
          :class="getActivityColor(activity.activity_type)"
        >
          <Icon :icon="getActivityIcon(activity.activity_type)" size="size-4" />
        </div>

        <!-- Content -->
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <div class="flex items-start justify-between gap-2">
            <span class="text-sm font-medium text-n-slate-12 line-clamp-2">
              {{ activity.title || activity.auto_description }}
            </span>
          </div>
          <p
            v-if="activity.description"
            class="text-xs text-n-slate-11 line-clamp-2"
          >
            {{ activity.description }}
          </p>
          <div class="flex items-center gap-2 mt-1">
            <span class="text-xs text-n-slate-10">
              {{
                formatActivityDate(
                  activity.activity_date || activity.created_at
                )
              }}
            </span>
            <span v-if="activity.user?.name" class="text-xs text-n-slate-10">
              {{
                t('COMPANY_DETAILS.SIDEBAR.TIMELINE.BY', {
                  name: activity.user.name,
                })
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
